import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
  ReactNode,
} from 'react';
import { UserManager, WebStorageStateStore, User, UserManagerSettings } from 'oidc-client-ts';
import { appSettings } from '../appSettings';
import { getBaseURL } from '../utils/AuthFunctionUtils';

type AuthContextType = {
  signIn: (args?: any) => Promise<void>;
  signInPopup: () => Promise<void>;
  signOut: () => Promise<void>;
  signOutRedirect: (args?: any) => Promise<void>;
  userManager: UserManager;
  userData: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
} | undefined;

export const AuthContext = React.createContext<AuthContextType>(undefined);


export const hasCodeInUrl = (location: Location): boolean => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

  return Boolean(
    searchParams.get('code') ||
    searchParams.get('id_token') ||
    searchParams.get('session_state') ||
    hashParams.get('code') ||
    hashParams.get('id_token') ||
    hashParams.get('session_state')
  );
};

// Get the base URL
const baseUrl = getBaseURL();

const OIDC_DEBUG_FLAG_KEY = 'acarshistory:oidc:debug';
const OIDC_DEBUG_TRACE_KEY = 'acarshistory:oidc:trace';
const OIDC_DEBUG_MAX_ENTRIES = 100;

const isOidcDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const query = new URLSearchParams(window.location.search);
  return query.get('oidcDebug') === '1' || localStorage.getItem(OIDC_DEBUG_FLAG_KEY) === '1';
};

const getStorageSnapshot = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const snapshot: Record<string, string> = {};
  Object.keys(localStorage)
    .filter((key) => key.toLowerCase().includes('oidc') || key.startsWith('acars'))
    .forEach((key) => {
      snapshot[key] = localStorage.getItem(key) || '';
    });

  return snapshot;
};

const writeDebugTrace = (entry: Record<string, unknown>) => {
  if (typeof window === 'undefined') {
    return;
  }

  const current = localStorage.getItem(OIDC_DEBUG_TRACE_KEY);
  const parsed = current ? JSON.parse(current) : [];
  const next = [...parsed, entry].slice(-OIDC_DEBUG_MAX_ENTRIES);
  localStorage.setItem(OIDC_DEBUG_TRACE_KEY, JSON.stringify(next));
};

const debugOidcStep = (step: string, extra: Record<string, unknown> = {}) => {
  if (!isOidcDebugEnabled() || typeof window === 'undefined') {
    return;
  }

  const payload = {
    at: new Date().toISOString(),
    step,
    href: window.location.href,
    host: window.location.hostname,
    env: appSettings.env,
    extra,
    storage: getStorageSnapshot(),
  };

  console.groupCollapsed(`[OIDC DEBUG] ${step}`);
  console.log(payload);
  console.groupEnd();

  writeDebugTrace(payload);
};

const debugBreak = (step: string) => {
  if (isOidcDebugEnabled()) {
    console.warn(`[OIDC DEBUG BREAK] ${step}`);
    debugger;
  }
};

if (typeof window !== 'undefined') {
  (window as any).__dumpAcarsOidcDebug = () => {
    const trace = localStorage.getItem(OIDC_DEBUG_TRACE_KEY);
    const parsed = trace ? JSON.parse(trace) : [];
    const storage = getStorageSnapshot();

    console.log('[OIDC DEBUG] trace entries:', parsed.length);
    console.log('[OIDC DEBUG] trace:', parsed);
    console.log('[OIDC DEBUG] storage:', storage);

    return { trace: parsed, storage };
  };
}

const getOidcProviderByEnv = (): string => {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const env = (appSettings.env || '').toUpperCase();

  // Prefer runtime host so TEST/QA deployments do not accidentally use PROD auth.
  if (
    host.includes('.test.') ||
    host.startsWith('test.') ||
    host.includes('.qa.') ||
    host.startsWith('qa.')
  ) {
    return 'https://fedauth.qa.alaskasworld.com';
  }

  if (env === 'TEST' || env === 'QA' || env === 'DEV') {
    return 'https://fedauth.qa.alaskasworld.com';
  }

  if (env === 'PROD') {
    return 'https://fedauth.alaskasworld.com';
  }

  return appSettings.OIDC_PROVIDER || '';
};

const getOidcClientIdByEnv = (): string => {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const env = (appSettings.env || '').toUpperCase();

  // Keep TEST/QA hostnames on non-PROD client id even when build-time env is PROD.
  if (
    host.includes('.test.') ||
    host.startsWith('test.') ||
    host.includes('.qa.') ||
    host.startsWith('qa.')
  ) {
    return 'c2e171a1-9695-4421-86bb-64f1afaed81f';
  }

  if (env === 'TEST' || env === 'QA' || env === 'DEV') {
    return 'c2e171a1-9695-4421-86bb-64f1afaed81f';
  }

  if (env === 'PROD') {
    return 'c2e171a1-9695-4421-86bb-64f1afaed81f';
  }

  return appSettings.OIDC_CLIENT_ID || '';
};

const getOidcRedirectRouteByEnv = (): string => {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const env = (appSettings.env || '').toUpperCase();

  if (
    host.includes('.test.') ||
    host.startsWith('test.') ||
    host.includes('.qa.') ||
    host.startsWith('qa.')
  ) {
    return '/acarshistory/callback';
  }

  if (env === 'TEST' || env === 'QA' || env === 'DEV') {
    return '/acarshistory/callback';
  }

  if (env === 'PROD') {
    return '/acarshistory/callback';
  }

  return appSettings.OIDC_REDIRECT_ROUTE || '';
};

const oidcProvider = getOidcProviderByEnv();
const oidcClientId = getOidcClientIdByEnv();
const oidcRedirectRoute = getOidcRedirectRouteByEnv();
const oidcRedirectUri = `${baseUrl}${oidcRedirectRoute}`;

debugOidcStep('oidc-config-resolved', {
  oidcProvider,
  oidcClientId,
  oidcRedirectRoute,
  oidcRedirectUri,
});

if (
  !oidcProvider ||
  !oidcClientId ||
  !oidcRedirectRoute ||
  !appSettings.OIDC_END_SESSION_ENDPOINT
) {
  throw new Error('OIDC settings are not properly defined in appSettings');
}

export const oidcConfig: UserManagerSettings = {
  authority: oidcProvider,
  client_id: oidcClientId,
  response_type: 'code',
  redirect_uri: oidcRedirectUri,
  silent_redirect_uri: oidcRedirectUri,
  post_logout_redirect_uri: oidcRedirectUri,
  loadUserInfo: true,
  automaticSilentRenew: true,
  scope: 'openid profile email',

  userStore: new WebStorageStateStore({ store: window.localStorage }),

  metadata: {
    issuer: oidcProvider,
    authorization_endpoint: `${oidcProvider}/as/authorization.oauth2`,
    token_endpoint: `${oidcProvider}/as/token.oauth2`,
    userinfo_endpoint: `${oidcProvider}/idp/userinfo.openid`,
    jwks_uri: `${oidcProvider}/pf/JWKS`,
    end_session_endpoint: appSettings.OIDC_END_SESSION_ENDPOINT || '',
  },
};

export const initUserManager = (): UserManager => {
  debugOidcStep('init-user-manager', {
    authority: oidcConfig.authority,
    client_id: oidcConfig.client_id,
    redirect_uri: oidcConfig.redirect_uri,
  });
  return new UserManager(oidcConfig);
};

type AuthProviderProps = {
  children: ReactNode;
  autoSignIn?: boolean;
  autoSignInArgs?: any;
  autoSignOut?: boolean;
  autoSignOutArgs?: any;
  onBeforeSignIn?: () => any;
  onSignIn?: (user: User | null) => void;
  onSignOut?: () => void;
  location?: Location;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  autoSignIn = true,
  autoSignInArgs,
  autoSignOut = true,
  autoSignOutArgs,
  onBeforeSignIn,
  onSignIn,
  onSignOut,
  location = window.location,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<User | null>(null);
  const [userManager] = useState(() => initUserManager());
  const isMountedRef = useRef(false);

 // const isAuthenticated = !!userData && !userData.expired; // Check if user is authenticated
    const isAuthenticated = !!userData;// For avoid empty blank page on first load
  const signOutHooks = useCallback(async () => {
    debugOidcStep('signout-hooks-start');
    setUserData(null);
    const storageKeys = Object.keys(localStorage);
    storageKeys.forEach((key) => {
      if (key.startsWith('oidc.user:')) {
        localStorage.removeItem(key);
      }
    });

   
    onSignOut && onSignOut();
    debugOidcStep('signout-hooks-end');
  }, [onSignOut]);

  const signInPopupHooks = useCallback(async () => {
    debugOidcStep('signin-popup-start');
    debugBreak('before-signin-popup');
    const userFromPopup = await userManager.signinPopup();
    setUserData(userFromPopup);
    onSignIn && onSignIn(userFromPopup);
    await userManager.signinPopupCallback();
    debugOidcStep('signin-popup-end', {
      userLoaded: Boolean(userFromPopup),
      subject: userFromPopup?.profile?.sub,
    });
  }, [userManager, onSignIn]);

  useEffect(() => {
    let isMounted = true;
    isMountedRef.current = true;
    setIsLoading(true);
    debugOidcStep('auth-effect-start', {
      autoSignIn,
      hasCode: hasCodeInUrl(location),
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });
    (async () => {
      try {
        const user = await userManager?.getUser();

        debugOidcStep('after-get-user', {
          hasUser: Boolean(user),
          expired: user?.expired,
          subject: user?.profile?.sub,
        });

        if (isMounted && (!user || user.expired)) {
          if (hasCodeInUrl(location)) {
            debugOidcStep('before-signin-callback');
            debugBreak('before-signin-callback');

            const callbackUser = (await userManager.signinCallback()) || null;
            setUserData(callbackUser);

            onSignIn && onSignIn(callbackUser);

            debugOidcStep('after-signin-callback', {
              hasUser: Boolean(callbackUser),
              subject: callbackUser?.profile?.sub,
            });
          } else if (autoSignIn) {
            const state = onBeforeSignIn ? onBeforeSignIn() : undefined;
            debugOidcStep('before-signin-redirect', {
              redirect_uri: oidcConfig.redirect_uri,
              authority: oidcConfig.authority,
              client_id: oidcConfig.client_id,
              hasState: Boolean(state),
            });
            debugBreak('before-signin-redirect');

            await userManager.signinRedirect({ ...autoSignInArgs, state });
          } else {
            debugOidcStep('no-autosignin-no-code');
          }
        } else if (isMountedRef.current) {
          setUserData(user);
          debugOidcStep('set-user-from-storage', {
            hasUser: Boolean(user),
            subject: user?.profile?.sub,
          });
        }
      } catch (error) {
        debugOidcStep('auth-effect-error', {
          error: error instanceof Error ? error.message : String(error),
        });
        debugBreak('auth-effect-error');
      } finally {
        setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
      isMountedRef.current = false;
      debugOidcStep('auth-effect-cleanup');
    };
  }, [location, userManager, autoSignIn, onBeforeSignIn, onSignIn]);

  useEffect(() => {
    const updateUserData = (user: User) => {
      if (isMountedRef.current) {
        setUserData(user);
        debugOidcStep('event-user-loaded', {
          subject: user?.profile?.sub,
          expired: user?.expired,
        });
      }
    };
    const onSilentRenewError = async () => {
      debugOidcStep('event-silent-renew-error');
      debugBreak('silent-renew-error');
      if (autoSignOut) {
        await signOutHooks();
        await userManager.signoutRedirect(autoSignOutArgs);
      }
    };
    userManager.events.addUserLoaded(updateUserData);
    userManager.events.addSilentRenewError(onSilentRenewError);
    return () => {
      userManager.events.removeUserLoaded(updateUserData);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [userManager, autoSignOut, autoSignOutArgs, signOutHooks]);

  const value = useMemo(
    () => ({
      signIn: async (args?: any) => {
        await userManager.signinRedirect(args);
      },
      signInPopup: async () => {
        await signInPopupHooks();
      },
      signOut: async () => {
        await userManager.removeUser();
        await signOutHooks();
      },
      signOutRedirect: async (args?: any) => {
        await userManager.signoutRedirect(args);
        await signOutHooks();
      },
      userManager,
      userData,
      isLoading,
      isAuthenticated, // Add isAuthenticated to the context value
    }),
    [userManager, isLoading, userData, isAuthenticated, signInPopupHooks, signOutHooks]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'AuthProvider context is undefined, please verify you are calling useAuth() as a child of an <AuthProvider> component.'
    );
  }

  return context;
};
